import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Send,
  Shield,
  ShieldCheck,
  UserCircle2,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { useCurrentUserProfile, useToggleAccountStatus } from '@/lib/api/hooks/admin-user';
import { initialsCase, titleCase } from '@/utils/helpers/textTransform';
import { showToast } from '@/lib/toast';
import { ProfileApiResponse } from '@/lib/api/services/admin-user';
import BlockUnblockModal from '@/pages/admin-users/modals/BlockUnblockModal';
import ResendInviteModal from '@/pages/admin-users/modals/ResendInviteModal';

type ProfileUser = ProfileApiResponse[number];
type TabKey = 'overview';

const tabItems: Array<{ key: TabKey; label: string }> = [
  { key: 'overview', label: 'Overview' },
];

const getRoleLabel = (role?: string) => {
  const normalized = role?.toUpperCase() || 'VIEWER';
  const map: Record<string, string> = {
    ADMIN: 'Administrator',
    ADMIN_STAFF: 'Staff Member',
    EDITOR: 'Editor',
    VIEWER: 'Viewer',
  };

  return map[normalized] || titleCase(role?.replace(/_/g, ' ')) || 'Viewer';
};

const getStatusLabel = (user?: ProfileUser) => {
  if (!user) {
    return { label: 'Unknown', className: 'bg-gray-100 text-gray-600 border-gray-200' };
  }

  if (!user.hasActivatedAccount) {
    return { label: 'Pending', className: 'bg-[#F7F1E7] text-[#9B6A1E] border-[#E4D1AC]' };
  }

  return { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
};

const UserDetails: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId?: string }>();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [isSuspendModalOpen, setSuspendModalOpen] = useState(false);
  const [isResendInviteModalOpen, setResendInviteModalOpen] = useState(false);
  const [isResendingInvite, setResendingInvite] = useState(false);

  const { data: currentUserProfile, isLoading, error, refetch } = useCurrentUserProfile();
  const toggleAccountStatusMutation = useToggleAccountStatus();

  const users = useMemo<ProfileUser[]>(() => {
    const rawData = currentUserProfile?.data as unknown;
    if (!Array.isArray(rawData)) return [];
    if (rawData.length > 0 && Array.isArray(rawData[0])) return rawData[0] as ProfileUser[];
    return rawData as ProfileUser[];
  }, [currentUserProfile]);

  const user = useMemo(() => {
    if (!users.length) return undefined;
    if (userId) {
      return users.find((item) => item.userId === userId) || users[0];
    }
    return users[0];
  }, [users, userId]);

  const displayName = `${user?.firstName || 'Unknown'} ${user?.lastName || 'User'}`.trim();
  const emailAddress = user?.emailAddress || '';
  const roleLabel = getRoleLabel(user?.userRole);
  const status = getStatusLabel(user);
  const avatarText = initialsCase(`${user?.firstName || ''} ${user?.lastName || ''}`) || '??';
  // const staffId = getStaffId(user);

  const handleBack = () => {
    navigate('/admin/admin-users');
  };

  const handleOpenSuspendModal = () => {
    setSuspendModalOpen(true);
  };

  const handleConfirmSuspend = async () => {
    if (!user) return;

    try {
      await toggleAccountStatusMutation.mutateAsync({
        userId: user.userId,
        blockStatus: true,
      });
      setSuspendModalOpen(false);
      refetch();
    } catch (mutationError) {
      showToast.error(mutationError instanceof Error ? mutationError.message : 'Failed to suspend user');
    }
  };

  const handleOpenResendInviteModal = () => {
    setResendInviteModalOpen(true);
  };

  const handleConfirmResendInvite = async () => {
    setResendingInvite(true);
    try {
      // Dummy flow until resend invite endpoint is available.
      await new Promise((resolve) => setTimeout(resolve, 900));
      showToast.success('Invitation resent successfully');
      setResendInviteModalOpen(false);
    } catch {
      showToast.error('Failed to resend invitation');
    } finally {
      setResendingInvite(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center rounded-xl bg-white">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-[#8F2F44]" />
          <p className="mt-4 text-sm text-gray-600">Loading staff profile...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="rounded-xl bg-white p-8 text-center shadow-sm">
        <h2 className="text-xl font-bold text-gray-900">Staff profile unavailable</h2>
        <p className="mt-2 text-sm text-gray-600">{error?.message || 'We could not find a user profile to display.'}</p>
        <Button variant="secondary" onClick={handleBack} className="mt-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Admin Users
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 mx-auto max-w-5xl">
      <div className="flex items-start justify-between gap-4">


        <Button
          variant="secondary"
          onClick={handleBack}
          className="rounded-lg border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Admin Users
        </Button>
      </div>

      <section className="rounded-xl border border-[#E7E2E4] bg-white p-5 shadow-[0_1px_4px_rgba(17,24,39,0.06)]">
        <div className="grid grid-cols-[1fr_auto] items-center">
          <div className="grid grid-cols-[auto_1fr] gap-4 items-center ">
            <div className="relative h-24 w-24 rounded-full">
              {user.profileImageUrl ? (
                <img src={user.profileImageUrl} alt={displayName} className="h-full w-full rounded-full object-cover" />
              ) : (
                <div className="flex h-full w-full rounded-full items-center justify-center bg-wine-100 text-3xl font-semibold text-white">
                  {avatarText}
                </div>
              )}
              <span className="absolute bottom-1 right-1 h-4 w-4 rounded-full border-2 border-white bg-emerald-500" />
            </div>

            <div className="space-y-2">
              <div>
                <h2 className="text-[27px] font-bold leading-none text-gray-900">{displayName}</h2>
                <p className="mt-2 flex items-center gap-2 text-[15px] text-gray-600">
                  <Mail className="h-4 w-4" />
                  {emailAddress}
                </p>
                <p className="mt-1.5 flex items-center gap-2 text-[15px] text-gray-600">
                  <Users className="h-4 w-4" />
                  {roleLabel}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2.5">
                <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700 ring-1 ring-emerald-200">
                  <span className="mr-2 h-2 w-2 rounded-full bg-emerald-500" />
                  {status.label}
                </span>
              </div>
            </div>
          </div>

        

          <div className="grid gap-4 sm:grid-cols-2 lg:border-l lg:border-[#ECE7E8] lg:pl-5">
     
            <Button
              type="button"
              onClick={handleOpenSuspendModal}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#C78D9A] bg-white text-sm font-semibold text-[#8F2F44] shadow-sm hover:bg-[#FBF7F8]"
            >
              <Shield className="h-4 w-4" />
              Suspend Access
            </Button>
            {!user.hasActivatedAccount && (
              <Button
                type="button"
                onClick={handleOpenResendInviteModal}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#8CBAA3] bg-white text-sm font-semibold text-[#24593E] shadow-sm hover:bg-[#F4FBF7]"
              >
                <Send className="h-4 w-4" />
                Resend Invite
              </Button>
            )}
      
          </div>
        </div>

      </section>

      {activeTab === 'overview' && (
        <div className="space-y-5">
          <section className="rounded-xl border border-[#E7E2E4] bg-white shadow-[0_1px_4px_rgba(17,24,39,0.06)]">
            <div className="px-5 pt-4">
              <div className="flex flex-wrap items-center gap-7 border-b border-[#ECE7E8]">
                {tabItems.map((tab) => {
                  const isActive = activeTab === tab.key;

                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => {
                        setActiveTab(tab.key);
                      }}
                      className={isActive ? 'relative pb-3 text-sm font-semibold text-[#8F2F44] after:absolute after:left-0 after:right-0 after:-bottom-[1px] after:h-[2px] after:rounded-full after:bg-[#8F2F44]' : 'pb-3 text-sm font-medium text-gray-700 hover:text-[#8F2F44]'}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-0 xl:grid-cols-2">
              <div className="border-b border-[#ECE7E8] p-5 xl:border-b-0 xl:border-r">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E0C8CF] bg-[#F8F2F4] text-[#8F2F44]">
                    <UserCircle2 className="h-5 w-5" />
                  </div>
                  <h3 className="text-[16px] font-bold text-gray-900">Profile Information</h3>
                </div>

                <div className="space-y-3 text-[14px]">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Full Name</p>
                    <p className="mt-1 font-medium text-gray-900">{displayName}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Email</p>
                    <p className="mt-1 font-medium text-gray-900">{emailAddress}</p>
                  </div>
            
            
                </div>
              </div>

              <div className="p-5">
                   <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E0C8CF] bg-[#F8F2F4] text-[#8F2F44]">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <h3 className="text-[16px] font-bold text-gray-900">Role & Access Summary</h3>
                </div>

             <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Role</p>
                      <p className="mt-2 font-medium text-[#8F2F44]">{roleLabel}</p>
                    </div>
             
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Access Level</p>
                      <span className="mt-2 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">Standard</span>
                    </div>
               
                  </div>
              </div>
            </div>
  
          </section>
        </div>
      )}

      {isSuspendModalOpen && (
        <BlockUnblockModal
          isOpen={isSuspendModalOpen}
          member={user}
          action="block"
          onClose={() => setSuspendModalOpen(false)}
          onConfirm={handleConfirmSuspend}
          isProcessing={toggleAccountStatusMutation.isPending}
        />
      )}

      {isResendInviteModalOpen && !user.hasActivatedAccount && (
        <ResendInviteModal
          isOpen={isResendInviteModalOpen}
          member={user}
          onClose={() => setResendInviteModalOpen(false)}
          onConfirm={handleConfirmResendInvite}
          isProcessing={isResendingInvite}
        />
      )}
    </div>
  );
};

export default UserDetails;
